(ns frontend.ux-overhaul.page-components.navigation.menu-items
    (:require
      [common.localization :as loc]
      [frontend.actions.reports :as actions-report]
      [frontend.bootstrap.components :as rbs]
      [frontend.routing :as routing]
      [frontend.state.current-session :as session]
      [frontend.tenant-customizations :as tenant-customizations]
      [frontend.ux-overhaul.page-components.navigation.events :as rf-events]
      [frontend.ux-overhaul.page-components.navigation.subs :as navigation-subs]
      [frontend.view.reports.util :as reports-util]
      [frontend.view.settings2.state :as state]
      [re-frame.core :as rf]
      [reagent.core :as r]
      [ui.util :as uu]))

(defn- session-unfinished-count
  "The count of the account's own unfinished tasks from the session. Correct on
   an old-grid account, where the tasks are bulk-synced."
  [access-to-all-tasks?]
  (let [my-unfinished-tasks @session/my-unfinished-tasks]
    (count (if access-to-all-tasks?
             my-unfinished-tasks
             (filter #(= (:task/type %) :task.type/simple) my-unfinished-tasks)))))

(defn- fetched-unfinished-count
  "The count of the account's own unfinished tasks from the backend. A new-grid
   account does not bulk-sync the tasks, so the badge reads the fetched counts."
  [access-to-all-tasks?]
  (let [counts @(rf/subscribe [::navigation-subs/task-badge-counts])]
    (if access-to-all-tasks?
      (reduce + 0 (vals counts))
      (get counts :task.type/simple 0))))

(defn bullet
  [navbar?]
  (r/with-let [_ (when (session/new-grid-2023?)
                   (rf/dispatch [::rf-events/load-task-badge-counts]))]
    (let [access-to-all-tasks? (or (session/dealflow?)
                                   (session/fundraising?)
                                   (session/sales-pipelines?))
          unfinished-count (if (session/new-grid-2023?)
                             (fetched-unfinished-count access-to-all-tasks?)
                             (session-unfinished-count access-to-all-tasks?))]
      (when-not (zero? unfinished-count)
        [rbs/Badge
         {:class (uu/cm "ml-1 p-1 bg-secondary-regular text-secondary-regular-text text-xs"
                        (when navbar? "absolute top-1 right-0"))
          :pill  true}
         unfinished-count]))))

(defn tasks-custom-label
  []
  [:span
   [:div (loc/tr :tasks)
    [bullet false]]])

(defn navbar-crm-items
  []
  (concat
    [{:navbar-item-key :navbar-item/crm-contacts
      :category-key    :crm
      :label           (loc/tr :contacts)
      :on-click!       #(rf/dispatch [::rf-events/navigate-to {:path :persons-grid}])}
     {:navbar-item-key :navbar-item/crm-companies
      :category-key    :crm
      :label           (loc/tr :companies)
      :on-click!       #(rf/dispatch [::rf-events/navigate-to {:path :companies-grid}])}]
    (when (and (not (session/is-read-only-user?))
               (session/tasks-access?))
      [{:navbar-item-key :navbar-item/crm-tasks
        :category-key    :crm
        :label           (loc/tr :tasks)
        :custom-label    [tasks-custom-label]
        :on-click!       #(rf/dispatch [::rf-events/navigate-to {:path :tasks-grid}])}])
    (when (session/has-investors-access?)
      [{:navbar-item-key :navbar-item/investors-search
        :on-click!       #(rf/dispatch [::rf-events/navigate-to {:path :investors-grid}])
        :label           (loc/tr :navbar-item/investors-search)
        :category-key    :crm}])
    #_(when (session/exit-process-management?)
        [{:navbar-item-key :navbar-item/buyer-profiles
          :on-click!       #(rf/dispatch [::rf-events/navigate-to {:path :buyers-grid}])
          :label           (loc/tr :navbar-item/buyer-profiles)
          :category-key    :crm}])
    (when (session/sales-pipelines?)
      [{:navbar-item-key :navbar-item/agreements
        :on-click!       (fn [_]
                           (session/set-module! :module/sales-pipelines)
                           (rf/dispatch [::rf-events/navigate-to {:path :sale-agreements-grid}]))
        :label           (loc/tr :sale-agreements)
        :category-key    :crm}])
    (when (session/has-feature-access? :feature/asset-types-real-estate)
      [{:navbar-item-key :navbar-item/real-estates
        :on-click!       #(rf/dispatch [::rf-events/navigate-to {:path :real-estates-grid}])
        :label           (loc/tr :real-estates)
        :category-key    :crm}])
    (when (session/has-feature-access? :feature/asset-types-fof)
      [{:navbar-item-key :navbar-item/third-party-funds
        :on-click!       #(rf/dispatch [::rf-events/navigate-to {:path :fof-assets-grid}])
        :label           (loc/tr :navbar-item/third-party-funds)
        :category-key    :crm}])
    (when (session/has-feature-access? :feature/asset-types-other)
      [{:navbar-item-key :navbar-item/generic-assets
        :on-click!       #(rf/dispatch [::rf-events/navigate-to {:path :other-assets-grid}])
        :label           (loc/tr :other-assets)
        :category-key    :crm}])
    (when (session/zap-vault?)
      [{:navbar-item-key :navbar-item/zap-vault
        :on-click!       #(rf/dispatch [::rf-events/navigate-to {:path :zap-vaults-grid}])
        :label           (loc/tr :navbar-item/zap-vault)
        :category-key    :crm}])))

(defn navbar-portfolio-management-items
  []
  (concat
    (when (session/portfolio?)
      [{:navbar-item-key :navbar-item/holdings
        :on-click!       (fn [_]
                           (session/set-module! :module/portfolio)
                           (rf/dispatch [::rf-events/navigate-to {:path :holdings-grid}]))
        :category-key    :portfolio-management
        :label           (loc/tr :holdings)}])
    (when (session/indirect-holdings?)
      [{:navbar-item-key :navbar-item/indirect-holdings
        :on-click!       (fn [_]
                           (session/set-module! :module/portfolio)
                           (rf/dispatch [::rf-events/navigate-to {:path :indirect-holdings-grid}]))
        :category-key    :portfolio-management
        :label           (loc/tr :indirect-holdings)}])
    (when (and (not (session/is-guest?))
               (session/kpi-reporting?))
      [{:navbar-item-key :navbar-item/portfolio-monitoring
        :on-click!       #(rf/dispatch [::rf-events/navigate-to {:path :kpi-categories}])
        :label           (loc/tr :navbar-item/portfolio-monitoring)
        :category-key    :portfolio-management}])))

(defn navbar-insight-and-repository-items
  []
  (concat
    (when (or (session/has-funds-access?)
              (and (session/fundraising?)
                   (= :plan/fundraising
                      (->> @session/tenant
                           :tenant/product-plan
                           :plan/type))))
      [{:navbar-item-key :navbar-item/commitments
        :on-click!       (fn [_]
                           (session/set-module! :module/fundraising)
                           (rf/dispatch [::rf-events/navigate-to {:path :fund-commitments-grid}]))
        :label           (loc/tr :commitments)
        :category-key    :insight-and-repository}])
    (when (session/has-funds-access?)
      [{:navbar-item-key :navbar-item/managed-funds
        :label           (loc/tr :navbar-item/managed-funds)
        :on-click!       (fn [_]
                           (session/set-module! :module/fundraising)
                           (rf/dispatch [::rf-events/navigate-to {:path :funds-grid}]))
        :category-key    :insight-and-repository}])
    (when (and (session/lp-reporting?)
               (not (tenant-customizations/capman?)))
      [{:navbar-item-key :navbar-item/lp-reporting
        :label           (loc/tr :navbar-item/lp-reporting)
        :on-click!       (fn [_]
                           (session/set-module! :module/lp-reporting)
                           (rf/dispatch [::rf-events/navigate-to {:path :lp-reports-grid}]))
        :category-key    :insight-and-repository}])
    (when (session/kyc?)
      [{:navbar-item-key :navbar-item/kyc
        :label           (loc/tr :kyc)
        :on-click!       #(rf/dispatch [::rf-events/navigate-to {:path (if (session/new-grid-2023?)
                                                                         :kyc-grid
                                                                         :kyc-grid-old)}])
        :category-key    :insight-and-repository}])
    (when (session/tenant-kyc?)
      [{:navbar-item-key :navbar-item/tenant-kyc
        :label           (loc/tr :tenant-kyc)
        :on-click!       #(rf/dispatch [::rf-events/navigate-to {:path :tenant-kyc-grid}])
        :category-key    :insight-and-repository}])
    (when (session/contract-kyc?)
      [{:navbar-item-key :navbar-item/contract-kyc
        :label           (loc/tr :contract-kyc)
        :on-click!       #(rf/dispatch [::rf-events/navigate-to {:path :contract-kyc-grid}])
        :category-key    :insight-and-repository}])
    (when (session/screening?)
      [{:navbar-item-key :navbar-item/screening
        :on-click!       #(rf/dispatch [::rf-events/navigate-to {:path (if (session/new-grid-2023?)
                                                                         :screening
                                                                         :screening-old)}])
        :label           (loc/tr :screening)
        :category-key    :insight-and-repository}])

    (when (session/counterparty-diligence-admin?)
      [{:navbar-item-key :navbar-item/project-counterparty-diligence
        :on-click!       #(rf/dispatch [::rf-events/navigate-to {:path :project-counterparty-diligence-grid}])
        :label           (loc/tr :counterparty-diligence/project-diligence)
        :category-key    :insight-and-repository}
       {:navbar-item-key :navbar-item/company-counterparty-diligence
        :on-click!       #(rf/dispatch [::rf-events/navigate-to {:path :company-counterparty-diligence-grid}])
        :label           (loc/tr :counterparty-diligence/company-diligence)
        :category-key    :insight-and-repository}
       {:navbar-item-key :navbar-item/person-counterparty-diligence
        :on-click!       #(rf/dispatch [::rf-events/navigate-to {:path :person-counterparty-diligence-grid}])
        :label           (loc/tr :counterparty-diligence/person-diligence)
        :category-key    :insight-and-repository}])
   (when (session/transaction-process?)
     [{:navbar-item-key :navbar-item/transaction-processes
       :on-click!       #(rf/dispatch [::rf-events/navigate-to {:path :transaction-processes-grid}])
       :label           (loc/tr :transaction-processes)
       :category-key    :insight-and-repository}])
   (when (and (not (session/is-guest?))
              (session/has-feature-access? :feature/ai-deal-parsing))
     [{:navbar-item-key :navbar-item/ai-summary-library
       :on-click!       #(rf/dispatch [::rf-events/navigate-to
                                       {:path :ai-summary-library}])
       :label           (loc/tr :ai-summary-library/title)
       :category-key    :insight-and-repository}])))

(defn navbar-survey-items
  []
  (when (session/surveys?)
    (->> @session/survey-types
         (map (fn [survey-type]
                {:navbar-item-key (keyword "navbar-item"
                                           (str "survey-"
                                                (:survey-type/label survey-type)))
                 :label           (:survey-type/label survey-type)
                 :on-click!       #(rf/dispatch [::rf-events/navigate-to
                                                 {:path [:surveys-grid :id (:db/id survey-type)]}])
                 :category-key    :surveys}))
         (doall))))

(defn navbar-reports-items
  []
  (when (or (session/dealflow?)
            (session/metabase-dashboards?))
    (concat
      (when (session/metabase-dashboards?)
        (->> @session/metabase-dashboards
             (sort-by :common/ord)
             (mapv (fn [md]
                     {:navbar-item-key (keyword "navbar-item" (str "metabase-" (:db/id md)))
                      :on-click!       (fn [_]
                                         (rf/dispatch [::rf-events/navigate-to {:path [:metabase-dashboard :id (:db/id md)]}]))
                      :label           (:metabase-dashboard/label md)
                      :category-key    :reports}))))
      (when (session/has-feature-access? :feature/old-dashboards)
        [{:navbar-item-key :navbar-item/reports-dashboard
          :on-click!       #(rf/dispatch [::rf-events/navigate-to {:path :dashboard}])
          :label           (loc/tr :reports/dashboard)
          :category-key    :reports}])
      [{:navbar-item-key :navbar-item/standard-reports
        :on-click!       #(rf/dispatch [::rf-events/navigate-to {:path :main-reports}])
        :label           (loc/tr :reports-default)
        :category-key    :reports}]
      (when (or (session/sys-admin?)
                (session/zadmin?)
                (some #{@session/tenant-name}
                      [#_"Zapflow" "Demo Tenant" "Demo Ventures" "TestReports" "Imec.xpand"]))
        [{:navbar-item-key :navbar-item/quarterly-reports
          :on-click!       (fn [_]
                             (routing/set-view! [:reports-printout
                                                 :report "imecs"
                                                 :token "preview"
                                                 :start-date "0"
                                                 :end-date "1"])
                             #_(let [origin (.-origin (.-location js/window))]
                                 (.open js/window (str origin "/printouts/imecs/preview/0/1"))))
          :label           (loc/tr :quarterly-reports)
          :category-key    :reports}])
      (when
        (or (tenant-customizations/demo-tenant?)
            (tenant-customizations/knight-therapeutics?)
            (tenant-customizations/panostaja?)
            (tenant-customizations/diamond-edge-ventures?)
            (tenant-customizations/capman-buyout-pipeline-access?))
        [{:navbar-item-key :navbar-item/custom-reports
          :on-click!       #(rf/dispatch [::rf-events/navigate-to {:path :custom-reports}])
          :label           (reports-util/report-title :custom-reports)
          :category-key    :reports}])
      (when (or (tenant-customizations/alj?)
                (tenant-customizations/diamond-edge-ventures?))
        [{:navbar-item-key :navbar-item/custom-reports-2
          :on-click!       #(rf/dispatch [::rf-events/navigate-to {:path :custom-reports-2}])
          :label           (reports-util/report-title :custom-reports-2)
          :category-key    :reports}])
      (when (or (tenant-customizations/capman?)
                (tenant-customizations/diamond-edge-ventures?)
                (tenant-customizations/demo-tenant?)
                (tenant-customizations/imec-xpand?))
        [{:navbar-item-key :navbar-item/custom-reports-3
          :on-click!       (fn [_]
                             (cond (tenant-customizations/capman?)
                                   (actions-report/*->fund-commit-contacts)
                                   :else
                                   (rf/dispatch [::rf-events/navigate-to {:path :custom-reports-3}])))
          :label           (reports-util/report-title :custom-reports-3)
          :category-key    :reports}]))))

(defn settings-items
  []
  (concat
    (when (and (session/is-admin?)
               (not (session/is-mobile?)))
      [{:navbar-item-key :navbar-item/settings
        :on-click!       #(rf/dispatch [::rf-events/navigate-to {:path [:account-settings :tab-key state/config-route]}])
        :label           (loc/tr :account-settings)
        :category-key    :menu-settings}])
    (when (and (not (session/is-guest?))
               (or (session/insider-group-admin?)
                   (session/surveys-admin?)
                   (session/metabase-dashboards-admin?)))
      [{:navbar-item-key :navbar-item/system-settings
        :on-click!       #(rf/dispatch [::rf-events/navigate-to {:path :system-settings}])
        :label           (loc/tr :navbar-menu/my-system-settings)
        :category-key    :menu-settings}])))

(defn all-available-navbar-items
  []
  (concat (navbar-crm-items)
          (navbar-portfolio-management-items)
          (navbar-insight-and-repository-items)
          (navbar-reports-items)
          (navbar-survey-items)
          (settings-items)))
